/**
 * Specimen Collection – Histopathology specimen list and sample collection popup.
 * Migrated from admin4.0 SpecimenCollection.js with child HistopathSpecimen.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Chip, FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { CommonGridHeader, CommonGridToolBarWithFilterText, CommonPatientDetails } from "../../Components/Common Components/CommonComponents";
import CommonValidation from "../../Utility/CommonValidation";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import { Colors } from "../../Styles/Colors";
import { CurrencySymbol } from "../../Utility/Constants";
import { ImagePaths } from "../../Utility/ImagePaths";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import { canEditTab } from "../../Utility/Services/RoleAccessService";
import { stripServiceHead } from "../../Utility/ServiceNameFormat";
import HistopathSpecimen from "./HistopathSpecimen";
import "../../Styles/Configuration.css";

export default function SpecimenCollection() {
  const [states, setState] = useState({
    specimenList: [],
    page: 0,
    pageSize: 10,
    fromDate: DateTime.now().toFormat("yyyy-MM-dd"),
    loading: false,
    SpecimenPopup: false,
    selectedBillId: null,
    datePicker: new Date(),
    isErrorMsg: false,
    isErrorMsgText: "",
    statusFilter: "pending",
    quickFilterValue: "",
  });

  const fetchSampleCollectionList = useCallback(() => {
    setState((prev) => ({ ...prev, loading: true }));
    const date = states.fromDate || DateTime.now().toFormat("yyyy-MM-dd");
    const url = `${Serviceurls.HISTOPATHOLOGY_SAMPLE_COLLECTION}${date ? `?date=${date}` : ""}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success") {
          setState((prev) => ({ ...prev, specimenList: res.data.data || [], loading: false }));
        } else {
          setState((prev) => ({ ...prev, specimenList: [], loading: false }));
        }
      })
      .catch((err) => {
        setState((prev) => ({
          ...prev,
          specimenList: [],
          loading: false,
          isErrorMsg: true,
          isErrorMsgText: err?.response?.data?.message || err?.message || "Failed to load sample collection list",
        }));
      });
  }, [states.fromDate]);

  useEffect(() => {
    fetchSampleCollectionList();
  }, [fetchSampleCollectionList]);

  const errorMessage = (msg) => {
    setState((prev) => ({ ...prev, isErrorMsg: true, isErrorMsgText: msg }));
  };

  const rows = useMemo(() => {
    const list = states.specimenList || [];
    const statusFilter = states.statusFilter || "all";
    const q = (states.quickFilterValue || "").trim().toLowerCase();
    const filtered = list.filter((element) => {
      const status = (element.status || "").toLowerCase();
      if (statusFilter !== "all" && status !== statusFilter) return false;
      if (q) {
        const pd = element.patient_details || {};
        const searchStr = [
          pd.name,
          pd.mobile_no,
          pd.patient_identifier,
          element.nxt_id,
          element.test_names,
          (element.status || "").toLowerCase(),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!searchStr.includes(q)) return false;
      }
      return true;
    });
    return filtered.map((element, index) => {
      const pd = element.patient_details || {};
      const pay = element.payment_status || {};
      const row = {
        ...element,
        sno: index + 1,
        bill_id: element.bill_id,
        name: pd.name,
        patients_mobile: pd.mobile_no,
        patient_account_number: element.nxt_id,
        age: pd.age,
        gender: pd.gender,
        patient_id: pd.patient_id,
        net_amount: pay.bill_amount ?? "0",
        balance_amount: pay.balance ?? "0",
        test_name: stripServiceHead(element.test_names ?? "-"),
        bill_date: element.bill_date || "",
        status: (element.status || "").toLowerCase(),
        is_action_enabled: !!element.is_action_enabled,
        patientdetils: `${CommonValidation.removeSpace(pd.name) ?? ""} ${pd.mobile_no ?? ""} ${pd.patient_identifier ?? ""}`.trim(),
      };
      return row;
    });
  }, [states.specimenList, states.statusFilter, states.quickFilterValue]);

  const columns = useMemo(
    () => [
      {
        field: "patientdetils",
        headerName: "Patient Details",
        flex: 0.25,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box mt={"0.5vw"}>
            <CommonPatientDetails data={params?.row} />
          </Box>
        ),
      },
      {
        field: "net_amount",
        headerName: "Payment Status",
        flex: 0.15,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box mt={"0.5vw"}>
            <Typography variant="body2" sx={{ fontSize: "0.8dvw" }}>
              Bill : {CommonValidation.AmountFormat(params.row.net_amount)}
            </Typography>
            <Typography variant="body2" color="error" sx={{ fontSize: "0.8dvw" }}>
              Balance : {CommonValidation.AmountFormat(params.row.balance_amount)}
            </Typography>
          </Box>
        ),
      },
      {
        field: "bill_date",
        headerName: "Bill Date & Time",
        flex: 0.14,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
          const iso = params?.row?.bill_date;
          if (!iso) return <Box sx={{ fontSize: "0.8dvw" }}>-</Box>;
          const dt = DateTime.fromISO(iso);
          return (
            <Box sx={{ lineHeight: 1.2 }}>
              <Typography variant="body2" sx={{ fontSize: "0.8dvw", fontWeight: 500 }}>
                {dt.isValid ? dt.toFormat("dd-LL-yyyy") : "-"}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.7dvw" }}>
                {dt.isValid ? dt.toFormat("hh:mm a") : ""}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "test_name",
        headerName: "Test Name",
        flex: 0.32,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box sx={{ fontSize: "0.8dvw" }}>
            {(params?.row?.test_name?.length ?? 0) > 80 ? (
              <Tooltip title={params?.row?.test_name} placement="top" arrow>
                <Box component="span">{params?.row?.test_name?.slice(0, 80) + "..."}</Box>
              </Tooltip>
            ) : (
              <Box>{params?.row?.test_name ?? "-"}</Box>
            )}
          </Box>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.1,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
          const isPending = params.value === "pending";
          return (
            <Chip
              label={isPending ? "Pending" : "Collected"}
              size="small"
              variant="outlined"
              sx={{
                minWidth: 100,
                backgroundColor: isPending ? "#FFF9F1" : "#F1FFF1",
                color: isPending ? "#E67E22" : "#2E7D32",
                borderColor: isPending ? "#E67E22" : "#2E7D32",
                fontWeight: 500,
                fontSize: "0.8dvw",
              }}
            />
          );
        },
      },
      {
        field: "action",
        headerName: "Actions",
        headerAlign: "left",
        align: "left",
        flex: 0.08,
        sortable: false,
        renderCell: (params) => {
          if (!canEditTab("Specimen Collection")) return null;
          return (
            <IconButton
              size="small"
              sx={{ minWidth: 40 }}
              onClick={() => {
                setState((prev) => ({
                  ...prev,
                  SpecimenPopup: true,
                  selectedBillId: params.row.bill_id,
                }));
              }}
            >
              <Box
                component="img"
                sx={{ width: "1.5dvw", height: "1.5dvw" }}
                src={ImagePaths.Speciman?.default ?? ImagePaths.Speciman}
                alt="specimen"
              />
            </IconButton>
          );
        },
      },
    ],
    []
  );

  const handleStatusFilterChange = (event) => {
    setState((prev) => ({ ...prev, statusFilter: event.target.value }));
  };

  const gridToolBar = () => (
    <Box
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      sx={{ position: "relative", zIndex: 5 }}
    >
      <CommonGridToolBarWithFilterText
        noExport
        searchHolder="Search"
        onChangingSearch
        value={states.quickFilterValue}
        onChangetrigger={(value) => setState((prev) => ({ ...prev, quickFilterValue: value || "", page: 0 }))}
      />
    </Box>
  );

  const msgClose = () => {
    setState((prev) => ({ ...prev, isErrorMsg: false, isErrorMsgText: "" }));
  };

  const handleCloseSpecimenPopup = () => {
    setState((prev) => ({
      ...prev,
      SpecimenPopup: false,
      selectedBillId: null,
    }));
  };

  return (
    <Box sx={{ width: "100%", height: "80vh", backgroundColor: Colors?.Background, padding: "0.5vw", boxSizing: "border-box" }}>
      <Box
        className="SpecimenCollection-page"
        sx={{ width: "100%", height: "84vh", backgroundColor: "white", display: "flex", flexDirection: "column", overflow: "hidden", boxSizing: "border-box" }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            position: "relative",
            zIndex: 10,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 2,
            p: 1.5,
            backgroundColor: "#fafafa",
            borderBottom: "1px solid #e0e0e0",
            minHeight: "8.5vh",
            flexShrink: 0,
            overflow: "visible",
          }}
        >
          <TextField
            size="small"
            type="date"
            label="Date"
            value={states.fromDate}
            onChange={(e) => setState((prev) => ({ ...prev, fromDate: e.target.value || DateTime.now().toFormat("yyyy-MM-dd") }))}
            sx={{ width: "10vw" }}
          />
          <FormControl size="small" sx={{ width: "10vw" }}>
            <InputLabel id="specimen-status-filter">Status</InputLabel>
            <Select
              labelId="specimen-status-filter"
              label="Status"
              value={states.statusFilter}
              onChange={handleStatusFilterChange}
              MenuProps={{
                disableScrollLock: true,
                disableAutoFocusItem: true,
                disableRestoreFocus: true,
                sx: { zIndex: 9999 },
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="collected">Collected</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => String(row.bill_id ?? row.sno)}
          paginationModel={{ page: states.page, pageSize: states.pageSize }}
          onPaginationModelChange={(model) => {
            setState((prev) => ({ ...prev, page: model.page, pageSize: model.pageSize }));
          }}
          pageSizeOptions={[10, 20, 30]}
          pagination
          rowHeight={52}
          columnHeaderHeight={40}
          disableRowSelectionOnClick
          loading={states?.loading}
          density="standard"
          showToolbar
          localeText={{
            toolbarColumns: "",
            toolbarDensity: "",
            toolbarExport: "",
            toolbarFilters: "",
            toolbarExportPrint: "",
          }}
          slots={{
            toolbar: CommonGridHeader.CommonToolBar,
            noRowsOverlay: () => (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                No Records To Be Shown
              </Box>
            ),
          }}
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            position: "relative",
            zIndex: 1,
            "& .MuiDataGrid-toolbarContainer": { position: "relative", zIndex: 5 },
            "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600, color: "#616161", fontSize: "0.8dvw" },
            "& .MuiDataGrid-cell": { fontSize: "0.8dvw" },
            "& .MuiDataGrid-footerContainer": {
              display: "flex !important",
              alignItems: "center !important",
              justifyContent: "flex-end !important",
              minHeight: "48px !important",
              padding: "0 12px !important",
              borderTop: "1px solid #e0e0e0",
            },
            "& .MuiTablePagination-root": {
              marginLeft: "auto !important",
              overflow: "hidden !important",
            },
            "& .MuiTablePagination-toolbar": {
              minHeight: "44px !important",
              alignItems: "center !important",
              flexWrap: "nowrap !important",
              justifyContent: "flex-end !important",
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-input": {
              margin: 0,
            },
            "& .MuiDataGrid-scrollbar--horizontal, & .MuiDataGrid-scrollbarFiller--horizontal": {
              display: "none !important",
              height: "0 !important",
            },
          }}
        />
      </Box>

      {states?.SpecimenPopup && (
        <HistopathSpecimen
          open={states.SpecimenPopup}
          onClose={handleCloseSpecimenPopup}
          onSaveSuccess={fetchSampleCollectionList}
          billId={states.selectedBillId}
          patientId={null}
          selectedDate={states.datePicker}
          errorMessage={errorMessage}
        />
      )}

      {states?.isErrorMsg && (
        <ToastMsg severity="error" msg={states.isErrorMsgText} msgPop={msgClose} />
      )}
    </Box>
  );
}
